import moment from "moment";
import { pluralize } from "humanize-plus";
import cloneDeep from "clone-deep";

import {
	EVENT_NEW_REPORTING_ENTITY,
	FEATURE_TYPES,
	CLASSIFICATION_TYPES,
	IDENTIFIER_TYPES
} from "constants.js";
import "./compliance.less";
import template from "./compliance.html";
import {
	sortBy,
	slice,
	find,
	findIndex,
	filter,
	forEach,
	some,
	size,
	reduce,
	uniqWith,
	keyBy
} from "lodash";
import vis from "vis";
import "vis/dist/vis-timeline-graph2d.min.css";
import { NEW_REPORT } from "routes";
import { getSeasonsByEffectiveRanges } from "../../utils/getSeasonsByEffectiveRanges";
import { isPeriodInEffectiveRanges } from "../../utils/effectiveRanges";
import {
	truncateReportPeriodToEffectiveRange, 
	truncateSeasonPeriodsToEffectiveRange 
} from "../../utils/effectiveRanges/truncateSeasonPeriodsToEffectiveRange";
import { DateRange } from "../../utils/moments/ranges";
import { fileNegativeModal } from "./modals/fileNegativeModal";
import { confirmNegativeWithPositives } from "./modals/confirmNegativeWithPositives";
import { 
	uncapitalize, 
	isStringNullishOrEmpty 
} from "../../services/string-service";

const periodNameMapper = {
	"January": "Jan",
	"February": "Feb",
	"March": "Mar",
	"April": "Apr",
	"June": "June",
	"July": "July",
	"August": "Aug",
	"September": "Sept",
	"October": "Oct",
	"November": "Nov",
	"December": "Dec"
};

// compliance
function controller(
	$scope,
	$timeout,
	$uibModal,
	$logger,
	appState,
	api,
	eventBus,
	loadingService,
	reportHelper,
	entityManager,
	timezoneService
) {
	const ctrl = this;

	const complianceClasses = {
		caution: "caution",
		compliant: "compliant",
		danger: "danger",
		open: "open",
		notStarted: "not-started",
		notOpened: "not-opened",
		openNotStartedNoneSubmitted: "open-not-started-none-submitted"
	};

	ctrl.$onInit = onInit;
	ctrl.fileNegative = fileNegative;
	ctrl.deleteNegative = deleteNegative;
	ctrl.getFormattedOpenDate = getFormattedOpenDate;
	ctrl.getEmptyTableString = getEmptyTableString;
	ctrl.getComplianceIcon = getComplianceIcon;
	ctrl.getComplianceColor = getComplianceColor;
	ctrl.setSelectedReportingIndex = setSelectedReportingIndex;
	ctrl.updateSeason = updateSeason;
	ctrl.tlSkipBack = tlSkipBack;
	ctrl.tlSkipNext = tlSkipNext;
	ctrl.tlZoomIn = tlZoomIn;
	ctrl.tlZoomOut = tlZoomOut;

	ctrl.tableWidthCss = "col-sm-9";
	ctrl.isAlertSummaryExpanded = window.innerWidth > 768;
	ctrl.timelineRange = undefined;
	ctrl.cachedReports = [];
	ctrl.alertSummaries = [];
	ctrl.showAllAlerts = false;
	ctrl.alertSummaryShowCount = 5;
	ctrl.orderedTableIds = [];
	ctrl.currentSeason = null;
	ctrl.selectedSeason = null;
	ctrl.seasons = []; // this is used in season dropdown mode only

	ctrl.NEW_REPORT = NEW_REPORT;

	ctrl.complianceIcons = {
		compliant: {
			icon: "zmdi-assignment-check",
			label: "Period is compliant"
		},
		pastDeadline: {
			icon: "zmdi-assignment-alert",
			label: "Period is out of compliance and past the deadline"
		},
		ended: {
			icon: "zmdi-assignment-alert",
			label: "Period is out of compliance but not past deadline"
		},
		notOpened: {
			icon: "zmdi-block",
			label: "Not opened"
		},
		open: {
			icon: "zmdi-forward",
			label: "Period is open for filing"
		},
		openNotCurrent: {
			icon: "zmdi-circle-o",
			label: "Open, not current"
		}
	};

	ctrl.complianceStatusColors = {
		compliant: "bfd-green",
		periodNotOpen: "bfd-dark-grey",
		periodNotStarted: "bfd-grey",
		pastDeadline: "text-danger",
		periodEndedWithNoReports: "bfd-caution",
		currentPeriodWithNoReports: "bfd-light-blue"
	};

	const defaultComplianceTable = {
		seasons: [],
		data: [],
		actions: {},
		sort: {
			predicate: "Period.BeginInclusive",
			direction: "asc"
		},
		grid: {
			paginationOptions:
			{
				itemsPerPage: "13",
				currentPage: 1
			}
		}
	};

	ctrl.paginationOptions = { itemsPerPage: "13", currentPage: 1 };
	ctrl.complianceTables = {};
	ctrl.pageSizes = [
		8, 12, 13, 26, 30, 52
	];
	ctrl.selectedReportIndex = "";

	function onInit() {
		ctrl.form = appState.forms.selected;
		ctrl.showTimeline = (ctrl.form.Features.filter(feature => feature.Type === FEATURE_TYPES.advancedCompliance).length > 0);
		ctrl.seasons = appState.forms.selected.Seasons;
		ctrl.permissions = appState.permissions;

		ctrl.negativeFields = ctrl.form.Fields.filter(f => !!f.IsNegative);

		eventBus.register(EVENT_NEW_REPORTING_ENTITY, () => {
			const newEntity = entityManager.getSelectedEntity();
			const promise = onReportingEntitySelectorUpdated(cloneDeep(newEntity.friendly));
			loadingService.watch(promise);
		}, $scope);

		// this is used instead of checking ctrl.isMobile, since that isn't reliably initialized at this point
		ctrl.legendIsExpanded = window.innerWidth > 768;
	}

	function configureVis() {
		const groups = [];
		let data = [];

		const visibleSeasons = ctrl.cachedReports
			.filter(
				x =>
					ctrl.timelineRange.end.isSameOrAfter(
						x.beginInclusive
					) ||
					ctrl.timelineRange.start.isBefore(x.endExclusive)
			)
			.map(x => x.seasonId);

		filter(ctrl.complianceTables,
			table =>
				visibleSeasons.some(visibleSeasonId =>
					table.seasons.some(
						seasonId => seasonId === visibleSeasonId
					))
		).forEach(table => {
			const group = { id: table.id, content: `<span class="group-name">${table.name}</span>` };
			groups.push(group);

			const results = table.data.map(x => {
				return {
					id: x.entityPeriodIndex,
					content: periodNameMapper[x.Period.Name] || x.Period.Name,
					start: x.Period.BeginInclusive,
					end: x.Period.EndExclusive,
					group: group.id,
					className: getComplianceClassName(x)
				};
			});

			data = [
				...data,
				...results
			];
		});

		if(size(ctrl.complianceTables) > 0) {
			ctrl.complianceTables[ctrl.orderedTableIds[0]].isExpanded = true;
		}
		const items = new vis.DataSet(data);

		if(!ctrl.timeline) {
			initializeTimeline(ctrl, items, groups);
		} else {
			ctrl.timeline.setGroups(groups);
			ctrl.timeline.setItems(items);
			$timeout(() => {
				$scope.$apply();
			});
		}
	}

	function deleteNegative(item, entityId) {
		const promise = reportHelper.delete(ctrl.form.Id, item.Negative)
			.then(() => {
				const thisTable = ctrl.complianceTables[entityId];

				for(let i = 0; i < thisTable.data.length; i++) {
					const dataItem = { ...thisTable.data[i] };
					if(dataItem.Period.Id === item.Period.Id) {
						dataItem.Compliant = item.Submitted > 0;
						dataItem.Negative = null;
						thisTable.data[i] = dataItem;
						break;
					}
				}
				updateTableDataRangeToTimeline();

				updateAlertSummaries();
				ctrl.showTimeline && configureVis();
			});

		loadingService.watch(promise);
	}

	ctrl.canFileNegative = (item) => {
		// TODO: switch permissions to new security globs
		const hasPermissionToFileNegative = 
			!ctrl.permissions.Reporting.IsDisabled 
			&& ctrl.permissions.Reporting.Actions.CanFileNegative;

		const hasStatusToFileNegative = 
			!item.Negative 
			&& (item.hasOpened || item.hasOpened === null) 
			&& !item.hasClosed;

		const isCompliantish = item.Compliant || item.Submitted > 0 || item.Pending > 0;
		const isDefaultSeasonScope = isStringNullishOrEmpty(item.seasonScope);

		return hasPermissionToFileNegative && hasStatusToFileNegative && (!isCompliantish || !isDefaultSeasonScope);
	};

	ctrl.canUndoNegative = (item) => {
		// TODO: switch permissions to new security globs
		return !ctrl.permissions.Reporting.IsDisabled 
			&& ctrl.permissions.Reporting.Actions.CanWithdrawNegative 
			&& !!item.Negative 
			&& (item.hasOpened || item.hasOpened === null) 
			&& !item.hasClosed;
	};

	function fileNegative(item, entityId) {
		const selectedEntity = find(ctrl.licenses, l => l.key === entityId);

		const isSelectedEntityHMS = !!selectedEntity && selectedEntity.classifications
			&& some(selectedEntity.classifications, c => c.Code === CLASSIFICATION_TYPES.TUNA
				|| c.Code === CLASSIFICATION_TYPES.SWORDFISH
				|| c.Code === CLASSIFICATION_TYPES.SHARK);

		const sendNegative = async() => {
			if(item.Pending > 0 || item.Submitted > 0) {
				const confirmed = await confirmNegativeWithPositives(ctrl.form.nomenclature, item.Submitted + item.Pending);
				
				if(!confirmed) {
					return;
				}
			}
		
			if(ctrl.negativeFields.length > 0 && !!selectedEntity && isSelectedEntityHMS) {
				const clickedTable = ctrl.complianceTables[entityId];
				const thisPeriodIndex = findIndex(clickedTable.data, d => d.Period.Id === item.Period.Id);
				const consecutivePeriods = findConsecutivePeriods(clickedTable.data, thisPeriodIndex, (item) => {
					return !item.Negative && item.hasOpened && !item.hasClosed;
				});

				const firstIndexOffset = findIndex(clickedTable.data, p => p.Period.Id === consecutivePeriods[0].Period.Id);

				const modal = fileNegativeModal($uibModal, ctrl);
				const response = await modal(entityId, thisPeriodIndex - firstIndexOffset, consecutivePeriods);

				if(response !== undefined) {
					const thisTable = clickedTable;
					const negatives = response;
					for(let i = 0; i < thisTable.data.length; i++) {
						const dataItem = { ...thisTable.data[i] };
						const newNegative = find(negatives, n => n.Periods[0].Id === dataItem.Period.Id);

						if(!!newNegative) {
							dataItem.Compliant = true;
							dataItem.Negative = newNegative;
							thisTable.data[i] = dataItem;
						}
					}

					updateTableDataRangeToTimeline();

					updateAlertSummaries();
					ctrl.showTimeline && configureVis();
				}
			}
			else {
				const negativePayload = {
					EntityId: entityId,
					FormId: ctrl.form.Id,
					Periods: [{ Id: item.Period.Id }]
				};

				const promise = api.post(`api/forms/${ctrl.form.Id}/reports/negative`, negativePayload);
				const response = await loadingService.watch(promise);
				
				const thisTable = ctrl.complianceTables[entityId];
				const negative = { Id: response[0].Id };

				for(let i = 0; i < thisTable.data.length; i++) {
					const dataItem = { ...thisTable.data[i] };
					if(dataItem.Period.Id === item.Period.Id) {
						dataItem.Compliant = true;
						dataItem.Negative = negative;
						thisTable.data[i] = dataItem;
						break;
					}
				}

				updateTableDataRangeToTimeline();

				updateAlertSummaries();
				ctrl.showTimeline && configureVis();
			}
		};
		
		sendNegative();
	}

	function findConsecutivePeriods(data, startingIndex, filterCallback) {
		const consecutiveData = [];
		for(let i = startingIndex; i >= 0; i--) {
			const isItemValid = filterCallback(data[i]);
			if(isItemValid) {
				consecutiveData.unshift(data[i]);
			}
			else {
				break;
			}
		}
		for(let i = startingIndex + 1; i < data.length; i++) {
			const isItemValid = filterCallback(data[i]);
			if(isItemValid) {
				consecutiveData.push(data[i]);
			}
			else {
				break;
			}
		}
		return consecutiveData;
	}

	function focusSelectedItem(item) {
		return new Promise((resolve) => {
			ctrl.timeline.moveTo(ctrl.timeline.itemsData._data[item].start, {}, () => {
				setTimeout(() => {
					ctrl.timeline.setSelection([item]);
					resolve();
				}, 50);
			});
		});
	}

	function getComplianceClassName(item) {
		return item.Compliant ? complianceClasses.compliant
			: item.hasOpened === false ? complianceClasses.notOpened
			: item.hasBegun === false && item.hasOpened ? complianceClasses.openNotStartedNoneSubmitted
			: item.hasBegun === false ? complianceClasses.notStarted
			: item.hasEnded === false ? complianceClasses.open 
			: item.isPastDeadline === false ? complianceClasses.caution
			: complianceClasses.danger;
	}

	function getComplianceColor(item) {
		return item.Compliant ? ctrl.complianceStatusColors.compliant
			: item.hasOpened === false ? ctrl.complianceStatusColors.periodNotOpen
			: item.hasBegun === false && item.hasOpened ? ctrl.complianceStatusColors.periodNotStarted
			: item.hasBegun === false ? ctrl.complianceStatusColors.periodNotStarted
			: item.hasEnded === false ? ctrl.complianceStatusColors.currentPeriodWithNoReports
			: item.isPastDeadline === false ? ctrl.complianceStatusColors.periodEndedWithNoReports
			: ctrl.complianceStatusColors.pastDeadline;
	}

	function getComplianceIcon(item, iconProp = "icon") {
		return item.Compliant ? ctrl.complianceIcons.compliant[iconProp]
		: item.hasOpened === false ? ctrl.complianceIcons.notOpened[iconProp]
		: item.hasBegun === false && item.hasOpened ? ctrl.complianceIcons.openNotCurrent[iconProp]
		: item.hasBegun === false ? ctrl.complianceIcons.openNotCurrent[iconProp]
		: item.hasEnded === false ? ctrl.complianceIcons.open[iconProp]
		: item.isPastDeadline === false ? ctrl.complianceIcons.ended[iconProp]
		: ctrl.complianceIcons.pastDeadline[iconProp];
	}

	function getEmptyTableString() {
		const primaryEntityFirstChar = ctrl.form.nomenclature.PrimaryEntity.substring(0, 1).toLowerCase();
		let article = "a";

		if(["a", "e", "i", "o", "u"].indexOf(primaryEntityFirstChar) > -1) {
			article = "an";
		}


		return `Please select ${article} ${ctrl.form.nomenclature.PrimaryEntity}`;
	}

	function getFormattedOpenDate(item) {
		return moment(item.Period.OpensInclusive).local()
			.format("MM.DD.YYYY, hh:mm A");
	}

	function setTimelineZoom(ctrl) {
		if(!(ctrl?.timeline?.itemsData))
			return;
		
		const data = ctrl.timeline.itemsData.get() ?? [];

		const momentify = (period) => ({ 
			start: moment(period.start), 
			end: moment(period.end) 
		});

		//from a point, build a range with begin/end = +/-quantity*'scale' 
		const rangify = (at, quantity, scale) => ({ 
			start: at.clone().subtract(quantity, scale), 
			end: at.clone().add(quantity, scale) 
		});

		//determine the duration between two moments in a range
		const durationOfRange = ({ start, end }) => ({ 
			duration: end.clone()
				.subtract(start)
				.valueOf() 
		});
			
		const dataAtMoment = (at) => data?.filter(period => at.isBetween(moment(period.start), moment(period.end))) ?? [];
		const dataInRange = ({ start, end }) => data?.filter(period => start.isSameOrBefore(moment(period.end)) && moment(period.start).isSameOrBefore(end)) ?? [];
		
		const now = moment();
		const minimumRange = rangify(now, 4, "days");
		const fallbackRange = rangify(now, 6, "months");

		//find relevant data to base time window on
		let inRangeData = dataAtMoment(now);
		if(!inRangeData.length) {
			for(const scale of ["days", "weeks", "months", "years"]) {
				inRangeData = dataInRange(rangify(now, 3, scale));
				if(inRangeData.length)
					break;
			}
		}

		//find the smallest window size for that data, or 6 months as default
		let dataWindowSize = inRangeData.reduce((agg, period) => { 
			const { duration } = durationOfRange(momentify(period));
			
			return duration > agg ? agg : duration;
		}, durationOfRange(fallbackRange).duration);

		//but not too small
		dataWindowSize = Math.max(durationOfRange(minimumRange).duration, dataWindowSize);
		
		//create a default window
		//data may  not fit, if we had to find via scaling, came across days as smallest
		//scope to days, but days aren't close to current
		let newWindow = rangify(now, dataWindowSize*3, "milliseconds");
		let growth = 0;
		while(!dataInRange(newWindow).length && ++growth < 20) {
			newWindow = rangify(now, dataWindowSize * 3 * Math.pow(1.5, growth), "milliseconds");
		}
		1;
		
		ctrl.timeline.setWindow(newWindow.start, newWindow.end);
	}

	function initializeTimeline(ctrl, items, groups) {
		if(!ctrl.container) {
			ctrl.container = document.getElementById("complianceTimeline");
		}

		ctrl.timeline = new vis.Timeline(ctrl.container, items, groups, {
			stack: false,
			zoomMax: 96410000000,	//~3 years
			zoomMin: 186400000,		//~2 days
			margin: {
				item: 10,
				axis: 5
			}
		});

		ctrl.timeline.on("click", onTimelineItemClick);
		ctrl.timeline.on("rangechanged", onTimelineRangeChanged);

		updateTableDataRangeToTimeline();
	}

	function loadComplianceReportForPrimary(primaryEntity) {
		const entitySeasons = primaryEntity.rangeSeasons ?? ctrl.seasons;
		let selectedSeasons = [];

		const currentDate = moment();

		ctrl.currentSeason = ctrl.seasons.filter((item) => currentDate.isSameOrAfter(item.BeginInclusive) && currentDate.isBefore(item.EndExclusive))[0];

		if(!ctrl.currentSeason) {
			ctrl.currentSeason = entitySeasons[entitySeasons.length - 1];
		}

		if(ctrl.showTimeline) {
			selectedSeasons = entitySeasons
				.filter(season => {
					if(!ctrl.timelineRange) {
						ctrl.timelineRange = {
							start: ctrl.currentSeason ? moment(ctrl.currentSeason.BeginInclusive) : moment(season.BeginInclusive),
							end: ctrl.currentSeason ? moment(ctrl.currentSeason.EndExclusive) : moment(season.EndExclusive)
						};
					}

					const seasonIsBeforeFrame = moment(season.EndExclusive).isBefore(ctrl.timelineRange.start);
					const seasonIsAfterFrame = moment(season.BeginInclusive).isAfter(ctrl.timelineRange.end);

					return !seasonIsAfterFrame && !seasonIsBeforeFrame;
				})
				.sort((a, b) => {
					if(moment(a.EndExclusive).isSame(b.EndExclusive)) {
						return 0;
					} else if(moment(a.EndExclusive).isAfter(b.EndExclusive)) {
						return -1;
					} else {
						return 1;
					}
				});

			if(!selectedSeasons.length) {
				selectedSeasons = [ctrl.currentSeason];
			}
		}
		else if(!!ctrl.selectedSeason?.Id) {
			selectedSeasons = [ctrl.selectedSeason];
		}
		else {
			selectedSeasons = [];
		}

		const seasonPromises = [];

		selectedSeasons.forEach(season => {
			const entityHasSeason = entitySeasons.map(s => s.Id).indexOf(season.Id) > -1;

			if(entityHasSeason) {
				const promise = getComplianceSeasonForEntity(season, primaryEntity);

				seasonPromises.push(promise);
			}
		});

		return Promise.all(seasonPromises);
	}

	function getPartialUnobscuredPeriod(rp, unobscuredRange) {
		if(unobscuredRange === undefined) {
			return rp;
		}

		const unobscuredReportPeriod = {
			...rp,
			BeginInclusive: unobscuredRange.start.format(),
			EndExclusive: unobscuredRange.end.format()
		};

		return unobscuredReportPeriod;
	}

	function getComplianceSeasonForEntity(season, entity) {
		if(ctrl.showTimeline) {
			if(ctrl.cachedReports.some(x => x.entityName === entity.Name && x.seasonId === season.Id)) {
				return Promise.resolve();
			}
		}
		else {
			ctrl.cachedReports = [];
		}

		ctrl.cachedReports.push({
			key: `${entity.Name}~${season.Id}`,
			entityName: entity.Name,
			beginInclusive: season.BeginInclusive,
			endExclusive: season.EndExclusive,
			seasonId: season.Id
		});
		
		const url = `api/forms/${ctrl.form.Id}/entities/${entity.Id}/compliance/seasons/${season.Id}`;

		const promise = api.get(url).then(unsorted => {
			const filtered = unsorted.filter(r => 
				entity.Effective?.length ? isPeriodInEffectiveRanges(r.Period, entity.Effective, season.Scope, ctrl.useFederalScopeOverride) : true
			);
			const resp = sortBy(filtered, r => moment(r.Period.BeginInclusive).unix());
				
			const now = moment();

			const unfilteredTableData = resp.map((report) => {
				if(entity.Effective) {
					report.Period = truncateReportPeriodToEffectiveRange(report.Period, season.Scope, entity.Effective, ctrl.useFederalScopeOverride);

					if(report.Period === undefined) {
						return null;
					}
				}
				const beginMoment = moment(report.Period.BeginInclusive);
				const endMoment = moment(report.Period.EndExclusive);

				const period = find(season.Periods, p => p.Id == report.Period.Id);

				if(period === undefined) {
					$logger.warn("Compliance Period not found in season", { season, report, ranges: entity.Effective, unsorted, resp, entity });
				}

				const partialUnobscuredPeriod =  getPartialUnobscuredPeriod(report.Period, period?.unobscuredRange);
				return {
					...report,
					Period: partialUnobscuredPeriod,
					entityPeriodIndex: `${entity.Id}~${report.Period.Id}`,
					Compliant: (report.Submitted > 0 || !!report.Negative),
					hasOpened: report.Period.OpensInclusive ? now.isSameOrAfter(moment(report.Period.OpensInclusive)) : null,
					hasClosed: report.Period.ClosesExclusive ? now.isAfter(moment(report.Period.ClosesExclusive)) : null,
					hasEnded: report.Period.EndExclusive ? now.isAfter(endMoment) : null,
					isPastDeadline: report.Period.Deadline ? now.isAfter(moment(report.Period.Deadline)) : null,
					hasBegun: report.Period.BeginInclusive ? now.isSameOrAfter(beginMoment) : null,
					seasonCode: season.Code,
					seasonName: season.Name,
					seasonScope: season.Scope,
					isObscured: period?.isObscured ?? false,
					seasonRangeLabel:
						// eslint-disable-next-line newline-per-chained-call
						`${moment.parseZone(report.Period.BeginInclusive).format("MM/DD/YYYY")} - ${moment.parseZone(report.Period.EndExclusive).subtract(1, "days")
							.format("MM/DD/YYYY")}`
				};
			})
				.filter(r => r !== null);

			const tableData = unfilteredTableData
				.filter((compliancePeriod) => compliancePeriod.hasOpened !== false && !compliancePeriod.isObscured);

			const matchingTable = ctrl.complianceTables[entity.Id];

			if(matchingTable) {
				matchingTable.seasons.push(season.Id);
				matchingTable.data.push(...tableData);
				matchingTable.data.sort(sortTableData);
				matchingTable.grid.paginationOptions.totalItems = matchingTable.data.length;
			}
			else {
				const now = moment();
				const currentPeriodIndex = findIndex(season.Periods, period => {
					return now.isSameOrAfter(moment(period.BeginInclusive)) && now.isBefore(moment(period.EndExclusive));
				});

				const currentPeriodPage = currentPeriodIndex !== undefined && (Math.floor(currentPeriodIndex / defaultComplianceTable.grid.paginationOptions.itemsPerPage) + 1);

				const newTable = {
					...defaultComplianceTable,
					seasons: [season.Id],
					name: entity.Name,
					id: entity.Id,
					data: tableData,
					displayData: [],
					timeRangedTableData: [],
					isExpanded: tableData.length > 0,
					grid: {
						paginationOptions: {
							...defaultComplianceTable.grid.paginationOptions,
							currentPage: currentPeriodPage || 1,
							totalItems: tableData.length
						}
					}
				};

				ctrl.complianceTables[entity.Id] = newTable;
			}

			pageComplianceTable(entity.Id);
		});
		loadingService.watch(promise);

		return promise;
	}

	function sortTableData(a, b) {
		const aMoment = moment(a.Period.BeginInclusive);
		const bMoment = moment(b.Period.BeginInclusive);

		if(aMoment.isBefore(bMoment)) {
			return -1;
		}
		else if(aMoment.isAfter(bMoment)) {
			return 1;
		}
		return 0;
	}

	function onTimelineItemClick(props) {
		if(props.what === "item") {
			setSelectedReportingIndex(props);

			$timeout(() => {
				$scope.$apply();
			});
		}
	}

	function onTimelineRangeChanged(props) {
		ctrl.timelineRange = {
			start: moment(props.start),
			end: moment(props.end)
		};
		
		renderComplianceReports({ selectedEntity: ctrl.selectedEntity }).then(() => updateTableDataRangeToTimeline());

		$timeout(() => {
			$scope.$apply();
		});
	}

	function updateTableDataRangeToTimeline() {
		forEach(ctrl.complianceTables, table => {
			let timeRangedTableData = table.data;
			if(ctrl.showTimeline) {
				timeRangedTableData = table.data.filter((period) =>
					moment(period.Period.EndExclusive).isSameOrAfter(moment(ctrl.timeline.range.start))
					&& moment(period.Period.BeginInclusive).isSameOrBefore(moment(ctrl.timeline.range.end)));
			}

			table.timeRangedTableData = timeRangedTableData;
			table.grid.paginationOptions.totalItems = table.timeRangedTableData.length;

			table.hideTable = false;
			if(table.timeRangedTableData.length === 0) {
				table.hideTable = true;
			}
			pageComplianceTable(table.id);
		});
	}

	function pageComplianceTable(tableId) {
		const table = find(ctrl.complianceTables, t => t.id === tableId);

		if(table.grid.paginationOptions.currentPage == 0) {
			table.grid.paginationOptions.currentPage = 1;
		}

		const totalItems = table.grid.paginationOptions.totalItems;
		const itemsPerPage = parseInt(table.grid.paginationOptions.itemsPerPage);
		const lastPage = parseInt((totalItems + itemsPerPage - 1) / itemsPerPage) || 1;
		if(table.grid.paginationOptions.currentPage > lastPage) {
			table.grid.paginationOptions.currentPage = lastPage;
		}

		const pagination = table.grid.paginationOptions;

		const startIndex = (pagination.currentPage - 1) * itemsPerPage;
		let endIndex = startIndex + itemsPerPage;
		if(endIndex > table.timeRangedTableData.length) {
			endIndex = table.timeRangedTableData.length;
		}

		table.displayData = slice(table.timeRangedTableData, startIndex, endIndex);
	}

	function renderComplianceReports(selection) {
		let useFriendlyEntity = !!selection.selectedEntity;
		
		if(selection.selectedEntity) {
			ctrl.selectedEntity = selection.selectedEntity;
		} else {
			ctrl.selectedEntity = cloneDeep(entityManager.getSelectedEntity());
			useFriendlyEntity = !!ctrl.selectedEntity.friendly;
		}

		if(ctrl.selectedEntity.friendly || ctrl.selectedEntity.primary) {
			ctrl.configuredTimezone = timezoneService.getConfiguredTimezoneCode(ctrl.selectedEntity);
			const entity = (useFriendlyEntity || entityManager.isEntitySelf(ctrl.selectedEntity.friendly))
				? ctrl.selectedEntity.friendly
				: ctrl.selectedEntity.primary;

			if(!entity || !entity.Id) {
				return;
			}

			return updateComplianceTables(entity).then(() => {
				ctrl.showTimeline && configureVis();
				updateTableDataRangeToTimeline();
				updateAlertSummaries();
			});
		}
		else {
			ctrl.showTimeline && configureVis();
			updateTableDataRangeToTimeline();
			updateAlertSummaries();
			return new Promise(resolve => resolve());
		}
	}

	function setSelectedReportingIndex(reportInformation, setSelection) {
		if(setSelection && ctrl.showTimeline) {
			focusSelectedItem(reportInformation.item).then(() => {
				updateTableDataRangeToTimeline();
				setTableItem();
			});
		}
		else {
			setTableItem();
		}

		function setTableItem() {
			ctrl.selectedReportIndex = reportInformation.item;

			forEach(ctrl.complianceTables, table => {
				const selectedItemIndex = sortBy(table.timeRangedTableData, x => Number(x.entityPeriodIndex.split("~")[1]))
					.findIndex(x => x.entityPeriodIndex == reportInformation.item);

				const selectedItemPage = Math.floor(selectedItemIndex / table.grid.paginationOptions.itemsPerPage) + 1;
				table.grid.paginationOptions.currentPage = selectedItemPage;
				pageComplianceTable(table.id);
			});
		}
	}

	function updateComplianceTables(entity) {
		const promises = [];

		loadAvailableSeasonsForEntity(ctrl.selectedEntity.friendly);

		if(ctrl.selectedEntity.friendly && ctrl.selectedEntity.friendly.Entities) {
			ctrl.selectedEntity.friendly.Entities.forEach(entity => {
				promises.push(loadComplianceReportForPrimary(entity.Entity));
			});
		} 
		else if(entity) {
			promises.push(loadComplianceReportForPrimary(entity));
		}

		return Promise.all(promises).then(() => {
			ctrl.orderedTables = sortBy(ctrl.complianceTables, t => t.id);
			ctrl.orderedTableIds = ctrl.orderedTables.map(t => t.id);

			ctrl.hasWarningIcon = some(ctrl.complianceTables, table =>
				table.data.some(item => item.isPastDeadline === false)
			);
			ctrl.hasOpenIcon = some(ctrl.complianceTables, table =>
				table.data.some(item => item.hasOpened)
			);
		});
	}

	function loadAvailableSeasonsForEntity(friendlyEntity) {
		const hasPrimaryEntityRanges = some(friendlyEntity.Entities ?? [], entity => !!entity.Entity.Effective?.length);
		const hasFriendlyEntityRanges = !!friendlyEntity.Effective?.length;

		let allSeasons = [];

		if(!hasPrimaryEntityRanges && !hasFriendlyEntityRanges) {
			allSeasons = appState.forms.selected.Seasons;
		}
		else if(hasPrimaryEntityRanges) {
			forEach(friendlyEntity.Entities, primary => {
				loadSeasonsForPrimaryEffectiveRanges(primary.Entity);
			});
		}
		else {
			loadSeasonsForPrimaryEffectiveRanges(friendlyEntity);
		}

		ctrl.seasons = uniqWith(allSeasons, (a, b) => a.Id === b.Id)
			.sort((a, b) => {
				if(moment(a.BeginInclusive).isBefore(moment(b.BeginInclusive))) {
					return -1;
				}
				else if(moment(a.BeginInclusive).isAfter(moment(b.BeginInclusive))) {
					return 1;
				}
				return 0;
			});

		if(!hasPrimaryEntityRanges) { 
			if(ctrl.hasFedIdentifier) {
				ctrl.seasons = ctrl.seasons.filter(s => s.Scope === "FED");
			}
			else {
				ctrl.seasons = ctrl.seasons.filter(s => s.Scope !== "FED");
			} 
		}
		
		forEach(ctrl.seasons, s => {
			const start = moment.parseZone(s.BeginInclusive)
				.format("MM/DD/YYYY");
			const end = moment.parseZone(s.EndExclusive)
				.subtract(1, "day")
				.format("MM/DD/YYYY");
			s.displayText = `${s.Name} ${ctrl.form.nomenclature.Season || "Season"} (${start} - ${end})`;
		});

		if(!ctrl.selectedSeason?.Id) {
			if(ctrl.seasons.length > 0) {
				const now = new Date();
				const currentSeason = ctrl.seasons.find(s => (new Date(s.BeginInclusive) <= now) && (new Date(s.EndExclusive) > now));
				const lastSeason = ctrl.seasons[ctrl.seasons.length-1];
			
				ctrl.selectedSeason = currentSeason ?? lastSeason;
				ctrl.lastSelectedSeasonId = ctrl.selectedSeason?.Id;
			}
		}
		
		function loadSeasonsForPrimaryEffectiveRanges(primary) {
			if(!!primary.Effective) {
				const primaryRangeSeasons = getSeasonsByEffectiveRanges(primary.Effective, appState.forms.selected.Seasons, ctrl.useFederalScopeOverride);

				const uniquePrimaryRangeSeasons = uniqWith(primaryRangeSeasons, (a, b) => a.Id === b.Id);
				allSeasons.push(...uniquePrimaryRangeSeasons);
				primary.rangeSeasons = uniquePrimaryRangeSeasons;

				primary.rangeSeasons = analyzeSeasonsPeriodsForOverlap(primary.rangeSeasons, primary.Effective);
			}
		}
	}

	function analyzeSeasonsPeriodsForOverlap(seasons, effectiveRanges) {
		const seasonWithInRangePeriods = seasons.map(s => {
			const periods = truncateSeasonPeriodsToEffectiveRange(s, effectiveRanges, ctrl.useFederalScopeOverride);

			return {
				...s,
				Periods: periods
			};
		});
		const periods = seasonWithInRangePeriods.flatMap(season => season.Periods);
		const periodOverlapCandidates = periods.map((period) => ({
			period: period,
			overlapsWith: detectOverlappingPeriodsOfGreaterSpecificity(periods, period).sort((periodA, periodB) => periodA.beginInclusiveMoment.unix() - periodB.beginInclusiveMoment.unix())
		}));

		const results = periodOverlapCandidates.map(check => ({
			...check,
			Id: check.period.Id,
			isObscured: isObscuredPeriod(check.period, check.overlapsWith)
		}));

		const keyedResults = keyBy(results, result => result.Id);

		periods.forEach(period => {
			period.isObscured = keyedResults[period.Id].isObscured.isObscured;
			period.unobscuredRange = keyedResults[period.Id].isObscured.obscuredRange !== undefined 
				? period.openRange.subtract(keyedResults[period.Id].isObscured.obscuredRange)[0]
				: undefined;
		});

		return seasonWithInRangePeriods;
	}


	function isObscuredPeriod(period, candidates) {
		if(!candidates.length) {
			return { isObscured: false };
		}

		//the earliest candidate begins AFTER this moment starts
		if(candidates[0].beginInclusiveMoment.isAfter(period.beginInclusiveMoment)) {
			if(candidates[candidates.length - 1].endExclusiveMoment.isSameOrAfter(period.endExclusiveMoment)) {
				const obscuredRange = new DateRange(candidates[0].BeginInclusive, period.EndExclusive);

				return { isObscured: false, obscuredRange };
			}
			else {
				throw new Error("2 unobscured sections of a period is not supported");
			}
		}
		
		//the latest candidate ends BEFORE this moment ends
		if(candidates[candidates.length - 1].endExclusiveMoment.isBefore(period.endExclusiveMoment)) {
			const obscuredRange = new DateRange(period.BeginInclusive, candidates[candidates.length - 1].EndExclusive);

			return { isObscured: false, obscuredRange };
		}

		//else the insanity
		//month:_____|xxxxxxxxxxxxxxxxxxxx|_______
		//week:___|xxxxx|xxxxx|xxxxx|xxxxx|xxxxx|_
		//month gets "obscured", if it is completely covered by other parts.  SOO

		//A: (take month) : __________|xxxxxxxxxxxxxxxx|_______
		//B: (minus week) : ___|xxxxx|xxxxx|xxxxx|xxxxx|xxxxx|_
		//and then repeat for each "candidate" and see if there's anything left over

		let periodsUnobscuredRange = period.openRange;
		for(const candidate of candidates) {
			//the candidate begins AFTER this moment starts
			if(candidate.beginInclusiveMoment.isAfter(periodsUnobscuredRange.start))
				return { isObscured: false };

			if(candidate.endExclusiveMoment.isBefore(periodsUnobscuredRange.start))
				continue;
				
			const diff = periodsUnobscuredRange.subtract(candidate.openRange);
			if(diff.length === 0) {
				return { isObscured: true };
			}
			if(diff.length > 1)
				throw new Error("This shouldn't occur since the candidate is supposed to begin BEFORE this time, but for some reason you got a fragment so have fun debugging that");
			periodsUnobscuredRange = diff[0];
		}

		return { isObscured: periodsUnobscuredRange[0] === null };
	}

	function detectOverlappingPeriodsOfGreaterSpecificity(periods, againstPeriod) {
		const candidates = [];
		for(const period of periods) {
			if(period.Id === againstPeriod.Id)
				continue;

			if(period.openRange.duration("minutes") > againstPeriod.openRange.duration("minutes"))
				continue;

			if(againstPeriod.openRange.overlaps(period.openRange, { adjacent: false })) {
				candidates.push(period);
			}
		}

		return candidates;
	}

	function updateAlertSummaries() {
		//show reports that are past deadline or have passed period end with no reports filed
		ctrl.alertSummaries = reduce(ctrl.complianceTables, (acc, curr) => {
			if(curr.timeRangedTableData.some(x => getComplianceClassName(x) === complianceClasses.caution)) {
				acc =
					[
						...acc,
						...curr.timeRangedTableData
							.filter(x => getComplianceClassName(x) === complianceClasses.caution)
							.filter(x => x.hasClosed !== true)
							.map(x => ({
								statusColor: ctrl.complianceStatusColors.periodEndedWithNoReports,
								statusIcon: ctrl.complianceIcons.ended.icon,
								statusLabel: ctrl.complianceIcons.ended.label,
								reportName: curr.name,
								group: curr.name,
								item: `${curr.id}~${x.Period.Id}`,
								message: `${x.Period.Name}: Near End of Period, No Completed ${pluralize(true, ctrl.form.nomenclature.PositiveReport)}`
							}))
					];
			}

			if(curr.timeRangedTableData.some(x => getComplianceClassName(x) === complianceClasses.danger)) {
				acc =
					[
						...acc,
						...curr.timeRangedTableData
							.filter(x => getComplianceClassName(x) === complianceClasses.danger)
							.filter(x => x.hasClosed !== true)
							.map(x => {
								return {
									statusColor: ctrl.complianceStatusColors.pastDeadline,
									statusIcon: ctrl.complianceIcons.pastDeadline.icon,
									statusLabel: ctrl.complianceIcons.pastDeadline.label,
									reportName: curr.name,
									group: curr.name,
									item: `${curr.id}~${x.Period.Id}`,
									message: `${x.Period.Name}: Past Deadline, No Completed ${pluralize(true, ctrl.form.nomenclature.PositiveReport)}`
								};
							})
					];
			}

			return acc;
		}, []);

		ctrl.hasAlerts = ctrl.alertSummaries.length > 0;
		ctrl.isAlertSummaryExpanded = ctrl.hasAlerts && !ctrl.isMobile;
	}

	function onReportingEntitySelectorUpdated(friendly) {
		ctrl.primaryReportingEntity = friendly || {};
		ctrl.hasFedIdentifier = false;
		ctrl.useFederalScopeOverride = false;

		if(friendly.Identifiers && some(friendly.Identifiers, i => IDENTIFIER_TYPES[i.Type.Code].isFederal)) {
			ctrl.hasFedIdentifier = true;
		}
		const areAnyPrimaryEffectiveRangesScoped = (friendly.Entities ?? []).some(e => (e.Entity.Effective ?? []).some(r => r.Scope !== undefined));
		const areAnyFriendlyEffectiveRangesScoped = (friendly.Effective ?? []).some(r => r.Scope !== undefined);

		if(ctrl.hasFedIdentifier && !areAnyFriendlyEffectiveRangesScoped && !areAnyPrimaryEffectiveRangesScoped) {
			ctrl.useFederalScopeOverride = true;
		}

		const validEntities = ctrl.primaryReportingEntity.Entities;

		const formSeasonIds = ctrl.form.Seasons.map(y => y.Id);
		ctrl.selectedSeason = null;

		const entityMapper = (primary, friendly) => {
			const seasonIds = primary.Effective?.length
			? getSeasonsByEffectiveRanges(primary.Effective, ctrl.form.Seasons, ctrl.useFederalScopeOverride)
				.map(y => y.Id)
			: formSeasonIds;

			return {
				key: primary.Id,
				display: primary.Code,
				seasonIds,
				primaryClassification: primary.Classification,
				classifications: ((primary.Classification && [primary.Classification]) ?? [])
					.concat((friendly?.Classification && [friendly.Classification]) ?? [])
					.concat(primary.Classifications ?? [])
					.concat(friendly?.Classifications ?? []),
				identifiers: (primary.Identifiers ?? []).concat(friendly?.Identifiers ?? [])
			};
		};

		if(!!validEntities) {
			ctrl.licenses = validEntities.map(x => entityMapper(x.Entity, friendly));
		}
		else {
			ctrl.licenses = [entityMapper(ctrl.primaryReportingEntity)];
		}

		ctrl.cachedReports = [];
		ctrl.complianceTables = {};

		const selected = !!friendly ? { friendly: { ...ctrl.primaryReportingEntity } } : null;

		return renderComplianceReports({ selectedEntity: selected })
			.then(() => {
				const now = moment();
				let selectedEntityIds = [selected.friendly.Id];
				if(selected.friendly.Entities) {
					selectedEntityIds = selected.friendly.Entities.map(e => e.Entity.Id);
				}

				forEach(selectedEntityIds, selectedEntityId => {
					const thisTable = ctrl.complianceTables[selectedEntityId];

					if(!!thisTable) {
						const currentPeriodIndex = findIndex(thisTable.data, period => {
							const isThis = now.isSameOrAfter(moment(period.Period.BeginInclusive)) && now.isBefore(moment(period.Period.EndExclusive));

							return isThis;
						});

						const currentPeriodPage = currentPeriodIndex !== undefined && (Math.floor(currentPeriodIndex / thisTable.grid.paginationOptions.itemsPerPage) + 1);

						thisTable.grid.paginationOptions.currentPage = currentPeriodPage || 1;
						thisTable.grid.paginationOptions.totalItems = thisTable.data.length;

						$scope.$watch(() => {
							if(ctrl.complianceTables[selectedEntityId]) {
								return ctrl.complianceTables[selectedEntityId].grid.paginationOptions.itemsPerPage;
							}
							return null;
						}, (next) => {
							if(next !== null) {
								if(ctrl.pageSet === undefined) {
									const watchCurrentPeriodIndex = findIndex(ctrl.complianceTables[selectedEntityId].data, period => {
										const isThis = now.isSameOrAfter(moment(period.Period.BeginInclusive)) && now.isBefore(moment(period.Period.EndExclusive));

										return isThis;
									});

									const currentPeriodPage = watchCurrentPeriodIndex !== undefined &&
										(Math.floor(watchCurrentPeriodIndex / defaultComplianceTable.grid.paginationOptions.itemsPerPage) + 1);
									ctrl.complianceTables[selectedEntityId].grid.paginationOptions.currentPage = currentPeriodPage || 1;
								}
								pageComplianceTable(selectedEntityId);
							}
						});

						$scope.$watch(() => {
							if(ctrl.complianceTables[selectedEntityId]) {
								return ctrl.complianceTables[selectedEntityId].grid.paginationOptions.currentPage;
							}
							return null;
						}, (next) => {
							if(next !== null) {
								ctrl.pageSet = next;
								pageComplianceTable(selectedEntityId);
							}
						});
						pageComplianceTable(selectedEntityId);
					}
				});
				updateTableDataRangeToTimeline();
			})
			.then(() => {
				$timeout(() => setTimelineZoom(ctrl));
			});
	}

	function updateSeason() {
		if(ctrl.selectedSeason?.Id === ctrl.lastSelectedSeasonId) {
			return;
		}

		ctrl.lastSelectedSeasonId = ctrl.selectedSeason?.Id;

		if(!ctrl.showTimeline) {
			ctrl.complianceTables = {};
		}

		renderComplianceReports({ selectedEntity: ctrl.selectedEntity }).then(() => {
			ctrl.orderedTableIds = ctrl.orderedTables
				.map(t => t.id);

			updateTableDataRangeToTimeline();
			pageComplianceTable(ctrl.orderedTableIds[0]);
		});
	}

	function tlSkipBack() {
		const {
			range,
			start
		} = getTimelineRange();

		const newWindow = {
			end: start.toDate(),
			start: start.subtract(range, "days").toDate()
		};

		ctrl.timeline.setWindow(newWindow);
	}

	function tlSkipNext() {
		const {
			range,
			end
		} = getTimelineRange();

		const newWindow = {
			start: end.toDate(),
			end: end.add(range, "days").toDate()
		};

		ctrl.timeline.setWindow(newWindow);
	}

	function tlZoomIn() {
		const {
			range,
			end,
			start
		} = getTimelineRange();

		const newRange = range * 0.333;
		const rangeDiff = range - newRange;

		const newWindow = {
			end: end.subtract((rangeDiff / 2), "days").toDate(),
			start: start.add((rangeDiff / 2), "days").toDate()
		};

		ctrl.timeline.setWindow(newWindow);
	}

	function tlZoomOut() {
		const {
			range,
			end,
			start
		} = getTimelineRange();

		const newRange = range * 1.5;
		const rangeDiff = newRange - range;

		const newWindow = {
			end: end.add((rangeDiff / 2), "days").toDate(),
			start: start.subtract((rangeDiff / 2), "days").toDate()
		};

		ctrl.timeline.setWindow(newWindow);
	}

	function getTimelineRange() {
		const timeWindow = ctrl.timeline.getWindow();

		const start = moment(timeWindow.start);
		const end = moment(timeWindow.end);
		const range = end.diff(start, "days");

		return { range, end, start };
	}

	ctrl.getNegativeTooltipText = (period) => {
		const pendingString = `${period.Pending} pending ${uncapitalize(pluralize(period.Pending, ctrl.form.nomenclature.PositiveReport))}`;
		const submittedString = `${period.Submitted} completed ${uncapitalize(pluralize(period.Submitted, ctrl.form.nomenclature.PositiveReport))}`;

		let combinedString;
		if(period.Pending > 0 && period.Submitted > 0) {
			combinedString = `${pendingString} and ${submittedString}`;
		}
		else if(period.Pending > 0) {
			combinedString = pendingString;
		}
		else if(period.Submitted > 0) {
			combinedString = submittedString;
		}

		const tooltip = `This period already has ${combinedString}.`;

		return tooltip;
	};
}

export default angular
	.module("vesl.screens.compliance", [])
	.component("complianceReport", {
		template,
		controller
	}).name;
